// client/src/components/auth/ResetPassword.js
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { Link, useSearchParams, useNavigate } from 'react-router-dom'
import ApiUsers from '../../api/ApiUsers'
import useAutofillSync from '../../hooks/useAutofillSync'

function ResetPassword() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState('')
  const [error, setError] = useState('')
  const [token, setToken] = useState('')

  // Refs for autofill detection
  const newPasswordRef = useRef(null)
  const confirmPasswordRef = useRef(null)

  // Sync autofilled passwords to state
  const handleAutofillSync = useCallback((values) => {
    if (values.newPassword) {
      setNewPassword(values.newPassword)
    }
    if (values.confirmPassword) {
      setConfirmPassword(values.confirmPassword)
    }
  }, [])

  // Memoize refs mapping to avoid re-running the effect on each render
  const autofillRefs = useMemo(
    () => ({
      newPassword: newPasswordRef,
      confirmPassword: confirmPasswordRef,
    }),
    [],
  )

  useAutofillSync(autofillRefs, handleAutofillSync)

  useEffect(() => {
    const tokenFromUrl = searchParams.get('token')
    if (tokenFromUrl) {
      setToken(tokenFromUrl)
    } else {
      setError('Invalid or missing reset token.')
    }
  }, [searchParams])

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    setError('')
    setMessage('')

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.')
      setLoading(false)
      return
    }

    try {
      const response = await ApiUsers.resetPassword({
        token,
        new_password: newPassword,
      })
      setMessage(response.message)
      // Redirect to sign-in after successful password reset
      setTimeout(() => {
        navigate('/sign-in')
      }, 2000)
    } catch (err) {
      if (err.message.includes('invalid_or_expired_token')) {
        setError(
          'This password reset link has expired or is invalid. Please request a new one.',
        )
      } else {
        setError(err.message || 'An error occurred. Please try again.')
      }
    } finally {
      setLoading(false)
    }
  }

  if (!token && !error) {
    return (
      <div className="container mt-5">
        <div className="text-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="container mt-5">
      <div className="row justify-content-center">
        <div className="col-md-6">
          <div className="card">
            <div className="card-body">
              <h2 className="text-center mb-4">Set New Password</h2>

              {message && (
                <div
                  className="alert alert-success"
                  data-testid="reset-password-success"
                >
                  {message}
                </div>
              )}
              {error && (
                <div
                  className="alert alert-danger"
                  data-testid="reset-password-error"
                >
                  {error}
                </div>
              )}

              {token && !message && (
                <form onSubmit={handleSubmit}>
                  <div className="mb-3">
                    <label className="form-label">New Password</label>
                    <input
                      ref={newPasswordRef}
                      type="password"
                      className="form-control"
                      data-testid="reset-password-new-input"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      required
                      placeholder="Enter new password"
                      aria-label="new-password"
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Confirm New Password</label>
                    <input
                      ref={confirmPasswordRef}
                      type="password"
                      className="form-control"
                      data-testid="reset-password-confirm-input"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                      placeholder="Confirm new password"
                      aria-label="confirm-password"
                    />
                  </div>

                  <div className="d-grid">
                    <button
                      className="btn btn-primary"
                      type="submit"
                      data-testid="reset-password-submit"
                      disabled={loading}
                    >
                      {loading ? 'Updating...' : 'Update Password'}
                    </button>
                  </div>
                </form>
              )}

              <div className="text-center mt-3">
                <Link to="/sign-in" className="text-decoration-none">
                  Back to Sign In
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ResetPassword
